import * as React from 'react'

import { Input } from '#/shared/ui/input'

import { useVinDecode } from '../model/use-vin-decode'
import type { VinDecodeResult } from '../model/use-vin-decode'

interface VinComboboxProps {
  value: string
  onValueChange: (value: string) => void
  onDecodedSelect?: (decoded: VinDecodeResult) => void
  onLoadingChange?: (loading: boolean) => void
  label?: string
  placeholder?: string
  error?: boolean
  className?: string
}

const VIN_LENGTH = 17

function VinSpinner() {
  return (
    <div
      role="progressbar"
      aria-label="Decoding VIN"
      className="size-3.5 animate-spin rounded-full border-2 border-current border-t-transparent text-gray-500"
    />
  )
}

export function VinCombobox({
  value,
  onValueChange,
  onDecodedSelect,
  onLoadingChange,
  label,
  placeholder,
  error,
  className,
}: VinComboboxProps) {
  const lastTypedRef = React.useRef<string | null>(null)
  const isUserTyped =
    lastTypedRef.current !== null && lastTypedRef.current === value
  const { decoded, isLoading } = useVinDecode(value, { enabled: isUserTyped })
  const lastAppliedRef = React.useRef<string | null>(null)

  React.useEffect(() => {
    if (value.trim().length === 0) {
      lastAppliedRef.current = null
      return
    }
    if (value.trim().length !== VIN_LENGTH) return
    const decodedVin = decoded?.vin
    if (!decodedVin) return
    if (lastAppliedRef.current === decodedVin) return
    lastAppliedRef.current = decodedVin
    onDecodedSelect?.(decoded)
  }, [value, decoded, onDecodedSelect])

  React.useEffect(() => {
    onLoadingChange?.(isLoading)
  }, [isLoading, onLoadingChange])

  return (
    <Input
      label={label}
      labelSize="sm"
      placeholder={placeholder}
      value={value}
      onChange={(e) => {
        lastTypedRef.current = e.target.value
        onValueChange(e.target.value)
      }}
      error={error}
      className={className}
      inputSize="sm"
      maxLength={VIN_LENGTH}
      clearable={!isLoading}
      endAdornment={isLoading ? <VinSpinner /> : undefined}
    />
  )
}
